import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type } from "arktype";
import { ArrowRight } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { toast } from "sonner";
import { AuthCard } from "@/components/auth/auth-card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useAppForm } from "@/hooks/useCreateForm";
import { authClient } from "@/lib/authClient";
import { cn } from "@/lib/clientUtils";
import { queryClient } from "@/lib/trpcClient";

const otpInput = type({
  code: "6<=string<=6",
});

export const Route = createFileRoute("/_unauthed/login/2fa")({
  component: RouteComponent,
  loader: async () => {
    authClient.twoFactor.sendOtp();
  },
});

function RouteComponent() {
  const { trpc } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const params = Route.useSearch();
  const posthog = usePostHog();

  const { mutate: resendOTP, isPending: resendCodeSubmitting } = useMutation({
    mutationFn: () => authClient.twoFactor.sendOtp(),
    onSuccess: (data) => {
      if (data.error) {
        toast.error("Login attempt expired, please retry login.");
        navigate({
          to: "/login",
        });
      } else {
        toast.success("Code resent");
      }
    },
  });

  const { mutate: verifyOtp, isPending: otpSubmitting } = useMutation({
    mutationFn: ({ code }: { code: string }) =>
      authClient.twoFactor.verifyOtp({ code }),
    async onSuccess(data) {
      if (data.error) {
        toast.error("Invalid or expired code");
      } else {
        await queryClient.refetchQueries(trpc.auth.session.queryOptions());

        const session = queryClient.getQueryData(
          trpc.auth.session.queryOptions().queryKey,
        )!;
        posthog.group("organization", session.org?.id!, {
          name: session.org?.dbaName ?? session?.org?.name!,
        });
        posthog.identify(session?.user?.id, {
          email: session?.user?.email,
          name: session?.user?.name,
        });

        posthog.capture("org_login", {
          distinct_id: session.org?.id,
          properties: {
            $process_person_profile: true,
          },
        });

        navigate({
          to: params.to ?? "/dashboard",
        });
      }
    },
  });

  const form = useAppForm({
    defaultValues: {
      code: "",
    },
    onSubmit: ({ value }) => {
      verifyOtp({ code: value.code });
    },
    onSubmitInvalid: () => {
      toast.error("Code must be six digits");
    },
    validators: {
      onSubmit: otpInput,
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <AuthCard
        description="Enter the 6-digit code sent to your phone number"
        title="Two-Factor Authentication"
      >
        <Form className="flex flex-col items-center gap-6">
          <form.AppField
            children={(field) => <field.OTPField label="One Time Password" />}
            name="code"
          />

          <Button
            className="min-w-[200px]"
            disabled={otpSubmitting}
            type="submit"
            variant="auth-primary"
          >
            {otpSubmitting ? "Verifying..." : "Verify Code"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>

          <button
            className={cn(
              "font-medium text-primary text-sm underline-offset-4 hover:text-primary/90 hover:underline",
              {
                "cursor-not-allowed opacity-50": resendCodeSubmitting,
              },
            )}
            disabled={resendCodeSubmitting}
            onClick={() => resendOTP()}
            type="button"
          >
            {resendCodeSubmitting ? "Sending..." : "Resend code"}
          </button>
        </Form>
      </AuthCard>
    </form>
  );
}
